export const INVOICE_PAYMENT_METHOD_FILTERS = [
  'cash',
  'card',
  'mixed',
  'invoice',
  'bank_transfer',
  'mobile_pay',
  'online',
  'other',
] as const;

export type InvoicePaymentMethodFilter = (typeof INVOICE_PAYMENT_METHOD_FILTERS)[number];

const METHOD_ORDER = ['cash', 'card', 'bank_transfer', 'invoice', 'mobile_pay', 'online', 'other'] as const;

function sortPaymentMethods(methods: string[]): string[] {
  const unique = [...new Set(methods.filter((method) => method !== 'reversal'))];
  return unique.sort((a, b) => {
    const orderA = (METHOD_ORDER as readonly string[]).indexOf(a);
    const orderB = (METHOD_ORDER as readonly string[]).indexOf(b);
    const rankedA = orderA === -1 ? 99 : orderA;
    const rankedB = orderB === -1 ? 99 : orderB;
    return rankedA - rankedB || a.localeCompare(b);
  });
}

/**
 * Keys for the invoice list "Maksutapa" cell. Prefer the payment trail
 * (so cash+card checkout renders as two methods); fall back to the
 * denormalized invoices.paymentMethod, including `mixed`.
 */
export function invoicePaymentMethodKeys(item: {
  paymentMethod?: string | null;
  paymentMethods?: string[] | null;
}): string[] {
  const fromPayments = sortPaymentMethods(item.paymentMethods ?? []);
  if (fromPayments.length > 0) return fromPayments;
  if (item.paymentMethod) return [item.paymentMethod];
  return [];
}

/**
 * Default method when recording a payment from the invoice pages (#4113).
 * Once the invoice has gone out, the money normally arrives as the invoice
 * being paid to the workshop's account, so that is suggested first. A draft
 * is still at the counter, where card stays the default like at checkout.
 */
export function recordPaymentInitialMethod(status: string): 'invoice' | 'card' {
  return status === 'draft' ? 'card' : 'invoice';
}

/**
 * Methods where the customer paid on the spot, so the receipt is the document
 * they get and there is no invoice left for them to pay (#4109):
 *
 * - `cash`, `card`, `mixed` (cash + card at the till, the denormalized value);
 * - `mobile_pay` and `online` too. `online` is what a paid Svea payment link
 *   records (`svea-payments/lifecycle.ts`): the customer paid by card or online
 *   bank right away, just like MobilePay, so a paid link is a receipt case.
 *   Until the link is paid the invoice is unpaid and keeps its print.
 *
 * `invoice` and `bank_transfer` mean the invoice itself was paid, and `other`
 * (and anything unknown) cannot be told apart, so those keep the invoice print.
 */
const PAID_ON_THE_SPOT_METHODS: ReadonlySet<string> = new Set([
  'cash',
  'card',
  'mixed',
  'mobile_pay',
  'online',
]);

export interface InvoicePrintState {
  totalCents: number;
  paidAmountCents: number;
  paymentMethod?: string | null | undefined;
  paymentMethods?: string[] | null | undefined;
  payments?: readonly { paymentMethod: string }[] | null | undefined;
}

/**
 * Whether the invoice print (invoice PDF, "Tulosta lasku", invoice copy) may
 * be offered (#4109). An invoice paid in full only by methods the customer
 * paid on the spot gets the receipt, the work order print and the delivery
 * note instead. Unpaid and partially paid invoices, invoices paid as invoices
 * (`invoice`, `bank_transfer`) and credit notes keep the invoice print. The
 * single rule for the till dialog, the Payments card, the invoice list and
 * the invoice page.
 */
export function isInvoicePrintOffered(invoice: InvoicePrintState): boolean {
  const paidInFull = invoice.totalCents > 0 && invoice.paidAmountCents >= invoice.totalCents;
  if (!paidInFull) return true;
  const methods = invoicePaymentMethodKeys({
    paymentMethod: invoice.paymentMethod ?? null,
    paymentMethods: invoice.payments
      ? invoice.payments.map((payment) => payment.paymentMethod)
      : (invoice.paymentMethods ?? null),
  });
  if (methods.length === 0) return true;
  return !methods.every((method) => PAID_ON_THE_SPOT_METHODS.has(method));
}
